import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import ConnectIndexGroupsInteractor from "../../organisations/interactors/ConnectIndexGroupsInteractor";
import ConnectIndexesInteractor from "../../organisations/interactors/ConnectIndexesInteractor";
import CreateOrganisationIndexGroupInteractor from "../../organisations/interactors/CreateOrganisationIndexGroupInteractor";
import CreateOrganisationIndexInteractor from "../../organisations/interactors/CreateOrganisationIndexInteractor";
import FindConnectedIndexGroupsInteractor from "../../organisations/interactors/FindConnectedIndexGroupsInteractor";
import FindConnectedIndexesInteractor from "../../organisations/interactors/FindConnectedIndexesInteractor";
import GetOrganisationIndexTreeInteractor from "../../organisations/interactors/GetOrganisationIndexTreeInteractor";
import { IOrganisation } from "../../organisations/model/Organisation";
import {
  IOrganisationIndex,
  TOrganisationIndex
} from "../../organisations/model/OrganisationIndex";
import { IOrganisationIndexCategory } from "../../organisations/model/OrganisationIndexCategory";
import {
  IOrganisationIndexGroup,
  TOrganisationIndexGroup
} from "../../organisations/model/OrganisationIndexGroup";
import OrganisationIndexTreePresentationModel, {
  TOrganisationIndexTreeItem
} from "../../organisations/presenter/model/OrganisationIndexTreePresentationModel";
import { IIndexType, TIndexType } from "../model/IndexType";
import { IIndexTypeGroup, TIndexTypeGroup } from "../model/IndexTypeGroup";

export interface IOrganisationIndexCategoryTree {
  [key: string]: TOrganisationIndexTreeItem[];
}
export interface IIndexTreeSelection {
  [key: string]: number;
}
export interface INewHarmonizedIndexItemRequest {
  [key: string]: string;
}

export interface IIndexTreeExpansion {
  [key: string]: string[];
}
export interface TCategoryIndexBulkEditorPresenter
  extends TLoadingAwarePresenter {
  categoryTypeId: number;
  createModalVisible?: boolean;
  organisations: IOrganisation[];
  indexGroupTypes: IIndexTypeGroup[];
  indexTypes: IIndexType[];
  groupTypeCandidates?: IIndexTypeGroup[];
  indexTypeCandidates?: IIndexType[];
  selectedGroupType?: IIndexTypeGroup;
  selectedIndexType?: IIndexType;
  selectedOrganisationIds?: number[];
  selectedOrganisationIdStrings?: string[];
  organisationIndexCategories: IOrganisationIndexCategory[];
  selectedCategories?: IOrganisationIndexCategory[];
  selectedIndexTrees?: IOrganisationIndexCategoryTree;
  treeSelection?: IIndexTreeSelection;
  expandedItems?: IIndexTreeExpansion;
  groupEditorModalVisible?: boolean;
  indexEditorModalVisible?: boolean;
  modalTitle?: string;
  newGroup?: INewHarmonizedIndexItemRequest;
  newIndex?: INewHarmonizedIndexItemRequest;
}
export interface ICategoryIndexBulkEditorPresenter
  extends TCategoryIndexBulkEditorPresenter,
    TPresentable {
  loadReferenceValues([organisations, organisationIndexCategories]: [
    organisations: IOrganisation[],
    organisationIndexCategories: IOrganisationIndexCategory[],
    indexGroupTypes: IIndexTypeGroup[],
    indexTypes: IIndexType[]
  ]): void;
  onSelectOrganisations(selectedOrganisations: string[]);
  onSelectTreeItem(node: TOrganisationIndexTreeItem): void;
  saveGroupChanges(isHarmonized: boolean, harmonizedLabel: string): void;
  saveIndexChanges(isHarmonized: boolean, harmonizedLabel: string): void;
  closeGroupEditorModal(): void;
  closeIndexEditorModal(): void;
  onNewIndexGroup(indexGroupType: TIndexTypeGroup): void;
  onNewIndex(indexType: TIndexType): void;
  onGroupNameTextChange(orgId: number, text: string);
  onIndexNameTextChange(orgId: number, text: string);
}
const defaultState: TCategoryIndexBulkEditorPresenter = {
  organisations: [],
  selectedOrganisationIds: [],
  organisationIndexCategories: [],
  selectedCategories: [],
  indexGroupTypes: [],
  indexTypes: [],
  groupEditorModalVisible: false,
  indexEditorModalVisible: false,
  modalTitle: "",
  newGroup: {},
  newIndex: {},
  categoryTypeId: -1
};
const CategoryIndexBulkEditorPresenter = withStore<
  ICategoryIndexBulkEditorPresenter,
  TCategoryIndexBulkEditorPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICategoryIndexBulkEditorPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCategoryIndexBulkEditorPresenter>();

    loader.start("organisationLoader");
    const loadReferenceValues = ([
      organisations,
      organisationIndexCategories,
      indexGroupTypes,
      indexTypes
    ]: [
      organisations: IOrganisation[],
      organisationIndexCategories: IOrganisationIndexCategory[],
      indexGroupTypes: IIndexTypeGroup[],
      indexTypes: IIndexType[]
    ]) => {
      let organisationIds = organisations.map((item) => item.id);
      let organisationIdStrings = organisationIds.map((id) => id.toString());
      _store.update({
        organisations,
        organisationIndexCategories,
        selectedOrganisationIds: organisationIds,
        selectedOrganisationIdStrings: organisationIdStrings,
        indexGroupTypes,
        indexTypes
      });
      loader.stop("organisationLoader");
      onSelectOrganisations(organisationIdStrings);
    };

    const onSelectOrganisations = async (selectedOrganisations: string[]) => {
      loader.start("indexLoader");
      const categories =
        _store.getState<ICategoryIndexBulkEditorPresenter>()
          .organisationIndexCategories;
      let organisationIds = selectedOrganisations.map((item) =>
        Number.parseInt(item, 10)
      );
      const selectedOrganisationIndexCategories = categories?.filter(
        (item) => organisationIds.indexOf(item.organisationId) !== -1
      );
      _store.update({
        selectedCategories: selectedOrganisationIndexCategories,
        selectedOrganisationIds: organisationIds,
        selectedOrganisationIdStrings: selectedOrganisations
      });

      const indexes = await Promise.all(
        selectedOrganisationIndexCategories.map((item) =>
          _application.container
            .resolve<GetOrganisationIndexTreeInteractor>(
              "getOrganisationIndexTree"
            )
            .execute(item.organisationId, item.id)
        )
      );

      let trees: IOrganisationIndexCategoryTree = {};
      indexes?.forEach((item) => {
        trees[item[0]?.organisationId] =
          OrganisationIndexTreePresentationModel(item);
      });

      _store.update({
        selectedIndexTrees: trees
      });
      loader.stop("indexLoader");
    };

    const onSelectTreeItem = async (node: TOrganisationIndexTreeItem) => {
      let organisationId = node.item.organisationId;
      let id = node.item.id;
      const { indexGroupTypes, indexTypes } =
        _store.getState<TCategoryIndexBulkEditorPresenter>();
      let groupTypeCandidates: TIndexTypeGroup[] = [];
      let indexTypeCandidates: TIndexType[] = [];
      let connections: IOrganisationIndex[] | IOrganisationIndexGroup[];
      if (node.type == "index") {
        connections = await _application.container
          .resolve<FindConnectedIndexesInteractor>("findConnectedIndexes")
          .execute(id, organisationId);
      } else {
        let groupTypeId = (node.item as IOrganisationIndexGroup)
          .indexTypeGroupId;
        const groupType = indexGroupTypes.find(
          (item) => item.id == groupTypeId
        );

        if (groupType) {
          groupTypeCandidates = indexGroupTypes.filter((item) =>
            groupType.childIds.includes(item.id)
          );
          indexTypeCandidates = indexTypes.filter((item) =>
            groupType.indexTypeIds.includes(item.id)
          );
        }
        connections = await _application.container
          .resolve<FindConnectedIndexGroupsInteractor>(
            "findConnectedIndexGroups"
          )
          .execute(id, organisationId);
      }

      let result: IIndexTreeSelection = {};
      result[organisationId.toString()] = id;
      connections.forEach((item) => {
        result[item.organisationId.toString()] = item.id;
      });
      _store.update({
        treeSelection: result,
        groupTypeCandidates,
        indexTypeCandidates
      });
    };

    const onNewIndexGroup = (indexGroupType: TIndexTypeGroup) => {
      _store.update({
        groupEditorModalVisible: true,
        modalTitle: translate("category_bulk_editor.add_new_group_action", {
          name: indexGroupType.name
        }),
        selectedGroupType: indexGroupType,
        selectedIndexType: undefined
      });
    };
    const onNewIndex = (indexType: TIndexType) => {
      _store.update({
        indexEditorModalVisible: true,
        modalTitle: translate("category_bulk_editor.add_new_index_action", {
          name: indexType.name
        }),
        selectedGroupType: undefined,
        selectedIndexType: indexType
      });
    };
    const closeGroupEditorModal = () => {
      _store.update({
        groupEditorModalVisible: false,
        newIndex: {},
        newGroup: {}
      });
    };
    const closeIndexEditorModal = () => {
      _store.update({
        indexEditorModalVisible: false,
        newIndex: {},
        newGroup: {}
      });
    };
    const onGroupNameTextChange = (orgId: number, text: string) => {
      let { newGroup } = _store.getState<TCategoryIndexBulkEditorPresenter>();
      if (newGroup) {
        newGroup[orgId.toString()] = text;
        _store.update({
          newGroup
        });
      }
    };
    const onIndexNameTextChange = (orgId: number, text: string) => {
      let { newIndex } = _store.getState<TCategoryIndexBulkEditorPresenter>();
      if (newIndex) {
        newIndex[orgId.toString()] = text;
        _store.update({
          newIndex
        });
      }
    };
    const saveGroupChanges = async (
      isHarmonized: boolean,
      harmonizedLabel: string
    ) => {
      const {
        newGroup,
        treeSelection,
        organisationIndexCategories,
        selectedGroupType,
        categoryTypeId
      } = _store.getState<TCategoryIndexBulkEditorPresenter>();
      if (!newGroup || !treeSelection) {
        return;
      }
      const languageId = _application.localeProvider.getCurrentLanguage();
      let createOrganisationIndexGroup =
        _application.container.resolve<CreateOrganisationIndexGroupInteractor>(
          "createOrganisationIndexGroup"
        );
      let connectIndexGroups =
        _application.container.resolve<ConnectIndexGroupsInteractor>(
          "connectIndexGroups"
        );
      loader.start("saveChanges");
      let groups: TOrganisationIndexGroup[] = await Promise.all(
        Object.keys(newGroup).map((orgId) => {
          let organisationId = Number.parseInt(orgId, 10);
          let parentId = treeSelection[orgId];
          let categoryId = organisationIndexCategories.find(
            (item) => item.organisationId === organisationId
          )?.id;
          return createOrganisationIndexGroup.execute({
            name: newGroup[orgId],
            nameTranslations: [
              {
                organisationId: organisationId,
                languageId,
                nameTranslation: newGroup[orgId],
                valid: true
              }
            ],
            organisationIndexCategoryId: categoryId,
            organisationId: organisationId,
            indexTypeGroupId: selectedGroupType?.id,
            parentId: parentId,
            valid: true,
            harmonized: isHarmonized
          } as any);
        })
      );

      let connectedGroups = await Promise.all(
        Object.keys(newGroup).map((orgId) => {
          let organisationId = Number.parseInt(orgId, 10);
          let groupId = groups?.find(
            (item) => item.organisationId == organisationId
          )?.id;

          let connections = Object.keys(newGroup)
            .filter((itm) => itm != orgId)
            .map((org) => {
              return {
                organisationId: org,
                id: groups?.find(
                  (item) => item.organisationId == Number.parseInt(org, 10)
                )?.id
              } as any;
            });
          return connectIndexGroups.execute(
            groupId as any,
            connections,
            organisationId,
            harmonizedLabel
          );
        })
      );

      _store.update({
        groupEditorModalVisible: false,
        newGroup: {},
        newIndex: {}
      });
      loader.stop("saveChanges");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("category_bulk_editor.save_changes_success"));
      _application.navigator.replace({
        pathname: `/category-editor/${categoryTypeId}`
      });
      return groups;
    };

    const saveIndexChanges = async (
      isHarmonized: boolean,
      harmonizedLabel: string
    ) => {
      const { newIndex, treeSelection, selectedIndexType, categoryTypeId } =
        _store.getState<TCategoryIndexBulkEditorPresenter>();
      if (!newIndex || !treeSelection) {
        return;
      }
      const languageId = _application.localeProvider.getCurrentLanguage();
      let createOrganisationIndexGroup =
        _application.container.resolve<CreateOrganisationIndexInteractor>(
          "createOrganisationIndex"
        );
      let connectIndexes =
        _application.container.resolve<ConnectIndexesInteractor>(
          "connectIndexes"
        );
      loader.start("saveChanges");
      let indexes: TOrganisationIndex[] = await Promise.all(
        Object.keys(newIndex).map((orgId) => {
          let organisationId = Number.parseInt(orgId, 10);
          let parentId = treeSelection[orgId];

          return createOrganisationIndexGroup.execute({
            name: newIndex[orgId],
            nameTranslations: [
              {
                organisationId: organisationId,
                languageId,
                nameTranslation: newIndex[orgId],
                valid: true
              }
            ],
            organisationId: organisationId,
            indexTypeId: selectedIndexType?.id,
            organisationGroupId: parentId,
            valid: true,
            harmonized: isHarmonized
          } as any);
        })
      );

      let connectedIndexes = await Promise.all(
        Object.keys(newIndex).map((orgId) => {
          let organisationId = Number.parseInt(orgId, 10);
          let groupId = indexes?.find(
            (item) => item.organisationId == organisationId
          )?.id;

          let connections = Object.keys(newIndex)
            .filter((itm) => itm != orgId)
            .map((org) => {
              return {
                organisationId: org,
                id: indexes?.find(
                  (item) => item.organisationId == Number.parseInt(org, 10)
                )?.id
              } as any;
            });
          return connectIndexes.execute(
            groupId as any,
            connections,
            organisationId,
            harmonizedLabel
          );
        })
      );

      _store.update({
        groupEditorModalVisible: false,
        newGroup: {},
        newIndex: {}
      });
      loader.stop("saveChanges");
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("category_bulk_editor.save_changes_success"));
      _application.navigator.replace({
        pathname: `/category-editor/${categoryTypeId}`
      });
      return indexes;
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      onSelectOrganisations,
      onSelectTreeItem,
      saveGroupChanges,
      saveIndexChanges,
      onNewIndexGroup,
      onNewIndex,
      closeGroupEditorModal,
      closeIndexEditorModal,
      onGroupNameTextChange,
      onIndexNameTextChange
    };
  },
  defaultState
);
export default CategoryIndexBulkEditorPresenter;
